import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { Database } from '@tallypg/contracts';
import { api, useApi } from '../api';

interface Column {
  name: string;
  type: string;
  nullable: boolean;
  default: string | null;
}
interface TableInfo {
  schema: string;
  name: string;
  rowEstimate: number;
  primaryKey: string[];
  columns: Column[];
}
interface Result {
  columns: { name: string; type: string }[];
  rows: unknown[][];
  rowCount: number;
  command: string;
  durationMs: number;
  truncated: boolean;
}

const fmt = (v: unknown): string =>
  v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);

/** Spreadsheet-style browser: pick a table, page through rows, edit cells in place, add or delete rows, export CSV. */
export function DatabaseDataPage() {
  const { databaseId } = useParams();
  const db = useApi<{ database: Database }>(`/databases/${databaseId}`);
  const tables = useApi<{ tables: TableInfo[] }>(`/databases/${databaseId}/tables`);
  const [table, setTable] = useState<TableInfo | null>(null);
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState<{ col: string; desc: boolean } | null>(null);
  const [filter, setFilter] = useState('');
  const [rows, setRows] = useState<Result | null>(null);
  const [editing, setEditing] = useState<{ r: number; c: number; value: string } | null>(null);
  const [newRow, setNewRow] = useState<Record<string, string> | null>(null);
  const [sql, setSql] = useState('');
  const [sqlResult, setSqlResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'grid' | 'sql'>('grid');
  const limit = 50;

  useEffect(() => {
    if (!table && tables.data?.tables[0]) setTable(tables.data.tables[0]);
  }, [tables.data, table]);

  const tableName = table ? `${table.schema}.${table.name}` : null;
  async function load() {
    if (!tableName) return;
    setError(null);
    try {
      const q = new URLSearchParams({ limit: String(limit), offset: String(page * limit) });
      if (orderBy) {
        q.set('orderBy', orderBy.col);
        q.set('desc', String(orderBy.desc));
      }
      if (filter.trim()) q.set('filter', filter.trim());
      setRows(await api<Result>(`/databases/${databaseId}/tables/${tableName}/rows?${q}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  }
  useEffect(() => {
    void load();
  }, [tableName, page, orderBy, databaseId]);

  const pkIndexes = useMemo(
    () => (table && rows ? table.primaryKey.map((k) => rows.columns.findIndex((c) => c.name === k)) : []),
    [table, rows],
  );
  const keyOf = (row: unknown[]) =>
    Object.fromEntries(pkIndexes.map((i) => [rows!.columns[i]!.name, row[i]]));
  const canEdit = table !== null && table.primaryKey.length > 0;

  async function saveCell() {
    if (!editing || !rows || !table) return;
    const col = rows.columns[editing.c]!.name;
    const row = rows.rows[editing.r]!;
    try {
      await api(`/databases/${databaseId}/tables/${tableName}/rows`, {
        method: 'PATCH',
        json: { key: keyOf(row), values: { [col]: editing.value === '' ? null : editing.value } },
      });
      setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  }

  function exportCsv(r: Result) {
    const esc = (v: unknown) => `"${fmt(v).replace(/"/g, '""')}"`;
    const text = [
      r.columns.map((c) => esc(c.name)).join(','),
      ...r.rows.map((row) => row.map(esc).join(',')),
    ].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
    a.download = `${table?.name ?? 'query'}.csv`;
    a.click();
  }

  if (db.error) return <p className="error">{db.error.message}</p>;
  return (
    <div>
      <p className="muted">
        <Link to="/databases">Databases</Link> /{' '}
        <Link to={`/databases/${databaseId}`}>{db.data?.database.name ?? '…'}</Link> / Data
      </p>
      <div className="row between">
        <h1>Data</h1>
        <div className="tabs" style={{ margin: 0 }}>
          <button className={tab === 'grid' ? 'active' : ''} onClick={() => setTab('grid')}>
            Tables
          </button>
          <button className={tab === 'sql' ? 'active' : ''} onClick={() => setTab('sql')}>
            SQL
          </button>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {tab === 'grid' && (
        <div className="data-layout">
          <aside className="card table-list">
            <h2>Tables</h2>
            {tables.data?.tables.length === 0 && (
              <p className="muted">No tables yet. Create one in the SQL tab or from your app.</p>
            )}
            {tables.data?.tables.map((t) => (
              <a
                key={`${t.schema}.${t.name}`}
                href="#"
                className={table?.name === t.name && table?.schema === t.schema ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  setTable(t);
                  setPage(0);
                  setOrderBy(null);
                  setFilter('');
                }}
              >
                {t.schema !== 'public' ? `${t.schema}.` : ''}
                {t.name} <span className="muted">~{t.rowEstimate.toLocaleString()}</span>
              </a>
            ))}
            <button className="secondary" style={{ marginTop: 10 }} onClick={() => void tables.reload()}>
              Refresh
            </button>
          </aside>
          <div className="card grid-card">
            {table && (
              <>
                <div className="row between" style={{ marginBottom: 8 }}>
                  <form
                    className="row"
                    onSubmit={(e) => {
                      e.preventDefault();
                      setPage(0);
                      void load();
                    }}
                  >
                    <input
                      placeholder="filter: column=value or column~text"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      style={{ width: 300 }}
                    />
                    <button type="submit" className="secondary">
                      Apply
                    </button>
                  </form>
                  <div className="row">
                    <button
                      className="secondary"
                      disabled={!canEdit}
                      onClick={() => setNewRow(Object.fromEntries(table.columns.map((c) => [c.name, ''])))}
                      title={canEdit ? '' : 'Tables need a primary key to edit here'}
                    >
                      Add row
                    </button>
                    <button className="secondary" disabled={!rows} onClick={() => rows && exportCsv(rows)}>
                      Export CSV
                    </button>
                  </div>
                </div>
                {!canEdit && (
                  <p className="muted">
                    This table has no primary key, so it is read-only here. Add one to edit rows in place.
                  </p>
                )}
                <div className="grid-scroll">
                  <table className="grid-table">
                    <thead>
                      <tr>
                        {rows?.columns.map((c) => (
                          <th
                            key={c.name}
                            onClick={() =>
                              setOrderBy({
                                col: c.name,
                                desc: orderBy?.col === c.name ? !orderBy.desc : false,
                              })
                            }
                            title={c.type}
                          >
                            {c.name} {orderBy?.col === c.name ? (orderBy.desc ? '▼' : '▲') : ''}
                          </th>
                        ))}
                        {canEdit && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {newRow && (
                        <tr className="new-row">
                          {table.columns.map((c) => (
                            <td key={c.name}>
                              <input
                                placeholder={c.default ? 'default' : c.nullable ? 'null' : c.type}
                                value={newRow[c.name] ?? ''}
                                onChange={(e) => setNewRow({ ...newRow, [c.name]: e.target.value })}
                              />
                            </td>
                          ))}
                          <td>
                            <button
                              onClick={async () => {
                                const values = Object.fromEntries(
                                  Object.entries(newRow).filter(([, v]) => v !== ''),
                                );
                                try {
                                  await api(`/databases/${databaseId}/tables/${tableName}/rows`, {
                                    method: 'POST',
                                    json: { values },
                                  });
                                  setNewRow(null);
                                  await load();
                                } catch (e) {
                                  setError(e instanceof Error ? e.message : 'Failed');
                                }
                              }}
                            >
                              Save
                            </button>{' '}
                            <button className="secondary" onClick={() => setNewRow(null)}>
                              Cancel
                            </button>
                          </td>
                        </tr>
                      )}
                      {rows?.rows.map((row, ri) => (
                        <tr key={ri}>
                          {row.map((cell, ci) => (
                            <td
                              key={ci}
                              onDoubleClick={() => canEdit && setEditing({ r: ri, c: ci, value: fmt(cell) })}
                            >
                              {editing && editing.r === ri && editing.c === ci ? (
                                <input
                                  autoFocus
                                  value={editing.value}
                                  onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') void saveCell();
                                    if (e.key === 'Escape') setEditing(null);
                                  }}
                                  onBlur={() => void saveCell()}
                                />
                              ) : (
                                <span className={cell === null ? 'muted' : ''}>
                                  {cell === null ? 'null' : fmt(cell)}
                                </span>
                              )}
                            </td>
                          ))}
                          {canEdit && (
                            <td>
                              <button
                                className="danger"
                                onClick={async () => {
                                  if (!confirm('Delete this row?')) return;
                                  try {
                                    await api(`/databases/${databaseId}/tables/${tableName}/rows`, {
                                      method: 'DELETE',
                                      json: { key: keyOf(row) },
                                    });
                                    await load();
                                  } catch (e) {
                                    setError(e instanceof Error ? e.message : 'Failed');
                                  }
                                }}
                              >
                                ×
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="row between" style={{ marginTop: 8 }}>
                  <span className="muted">
                    rows {page * limit + 1}–{page * limit + (rows?.rows.length ?? 0)}
                    {rows?.truncated ? ' (more available)' : ''} · double-click a cell to edit
                  </span>
                  <div className="row">
                    <button className="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>
                      Previous
                    </button>
                    <button
                      className="secondary"
                      disabled={!rows || rows.rows.length < limit}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {tab === 'sql' && (
        <div className="card">
          <h2>SQL</h2>
          <p className="muted">
            One statement at a time, run as your database user. Results are capped at 500 rows; use your own
            tools for big exports.
          </p>
          <textarea
            rows={6}
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            placeholder="select * from my_table limit 10"
            style={{ fontFamily: 'var(--mono)' }}
          />
          <div className="row" style={{ marginTop: 8 }}>
            <button
              onClick={async () => {
                setError(null);
                try {
                  setSqlResult(
                    await api<Result>(`/databases/${databaseId}/query`, {
                      method: 'POST',
                      json: { sql, maxRows: 500 },
                    }),
                  );
                  void tables.reload();
                } catch (e) {
                  setError(e instanceof Error ? e.message : 'Failed');
                }
              }}
            >
              Run
            </button>
            {sqlResult && (
              <>
                <span className="muted">
                  {sqlResult.command} · {sqlResult.rowCount} row{sqlResult.rowCount === 1 ? '' : 's'} ·{' '}
                  {sqlResult.durationMs} ms{sqlResult.truncated ? ' · truncated' : ''}
                </span>
                {sqlResult.rows.length > 0 && (
                  <button className="secondary" onClick={() => exportCsv(sqlResult)}>
                    Export CSV
                  </button>
                )}
              </>
            )}
          </div>
          {sqlResult && sqlResult.columns.length > 0 && (
            <div className="grid-scroll" style={{ marginTop: 10 }}>
              <table className="grid-table">
                <thead>
                  <tr>
                    {sqlResult.columns.map((c) => (
                      <th key={c.name} title={c.type}>
                        {c.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sqlResult.rows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j}>{cell === null ? <span className="muted">null</span> : fmt(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
